import React, { useEffect, useState } from 'react';
import { RegionCode, getRegion } from '../utils/region';
// Brand mark used site-wide (navbar, mobile menu). Each domain ships ONE
// artwork, exported BLACK — it renders as-is on light surfaces and is inverted
// to white on dark ones.
//
// On load the mark holds its black/white state for a beat, then settles into
// brand green and stays there for the rest of the visit. The change is a
// crossfade between two stacked copies of the SAME artwork (an <img> and a
// mask-filled block), so only `opacity` animates — that's GPU-composited, with
// no repaint cost. Animating a CSS filter or color instead would re-rasterize
// the mark on every frame.
const LOGO_BY_REGION: Record<RegionCode, {
  light: string;
  dark: string;
}> = {
  '253': {
    light: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png'
  },
  '206': {
    // Single white vector artwork. SVG stays crisp at every size, unlike the
    // raster PNGs it replaced.
    light: 'https://cdn.magicpatterns.com/uploads/bGxoW4WtWCUnBwpobWRxPH/206LOGO.svg',
    dark: 'https://cdn.magicpatterns.com/uploads/bGxoW4WtWCUnBwpobWRxPH/206LOGO.svg'
  },
  '425': {
    light: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png'
  }
};
/**
 * Resolve the active domain's logo image URL.
 */
export function getLogoSrc(region?: RegionCode, surface: 'light' | 'dark' = 'dark'): string {
  return LOGO_BY_REGION[region ?? getRegion()][surface];
}
// TEMPORARY GLOBAL HIDE — every logo across the site is suppressed
// while this flag is true (the Logo component below, plus the inline
// <img> logos in Navbar.tsx that import this constant). To restore the
// logos, flip this back to `false` and re-publish.
export const LOGOS_HIDDEN = false;
// On light surfaces the inverted-to-black artwork is held at 80% opacity so the
// solid black reads a touch softer against the navbar. On dark surfaces the
// white artwork renders at full strength.
const LOGO_OPACITY_CLASS = 'opacity-80 dark:opacity-100';
// Deep brand green the mark settles into.
const TINT_COLOR = '#006239';
// Beat before the fade starts, and the length of the fade itself.
const TINT_DELAY_MS = 1000;
const TINT_FADE_MS = 10000;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  /**
   * The artwork is exported white. Leave this true to invert it to black on
   * light surfaces; pass false for call sites that are ALWAYS dark, so the
   * white artwork is used in both themes.
   */
  invertOnLight?: boolean;
  /**
   * Retained for backwards-compatibility with existing call sites. The
   * black/white → green settle is driven internally, so these no longer
   * affect rendering.
   */
  cycleEvery?: number;
  region?: RegionCode;
  pinned?: boolean;
  tinted?: boolean;
  tintColor?: string;
}
/**
 * 206Gravel brand mark — one artwork that settles from black/white into brand
 * green shortly after load and holds green until the next page load.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  tintColor = TINT_COLOR
}: LogoProps) {
  // `green` drives the crossfade; `animate` is false for visitors who ask for
  // reduced motion, so they get the green mark immediately with no transition.
  const [green, setGreen] = useState(false);
  const [animate, setAnimate] = useState(true);
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setAnimate(false);
      setGreen(true);
      return;
    }
    const timer = setTimeout(() => setGreen(true), TINT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  // While LOGOS_HIDDEN is true, every Logo instance renders nothing.
  // Flip the constant above to `false` (or remove this block) to restore.
  if (LOGOS_HIDDEN) return null;
  const src = getLogoSrc(getRegion(), 'light');
  const fade = animate ? 'transition-opacity ease-in-out' : '';
  const fadeStyle = animate ? {
    transitionDuration: `${TINT_FADE_MS}ms`
  } : undefined;
  return <span className={`relative inline-flex items-center ${className}`} data-id="element-1176" data-mp="ghuglbijnqw4">
      {/* Base artwork — black on light surfaces, white on dark. Fades out as
       the green copy fades in so the mid-transition read stays clean (leaving
       it visible underneath would leave a dark rim on the antialiased edges). */}
      <img src={src} alt="206Gravel" style={fadeStyle} className={`${imgClassName} block pointer-events-none ${fade} ${green ? 'opacity-0' : LOGO_OPACITY_CLASS} ${invertOnLight ? 'dark:invert' : 'invert'}`} data-id="element-1177" data-mp="v7jlnhtrcf1o" />
      {/* Green copy — the same file used as a CSS mask so it can be tinted
       (an <img> can only be inverted, not colored). Purely decorative: the
       <img> above carries the accessible name. */}
      <span aria-hidden="true" style={{
      ...fadeStyle,
      backgroundColor: tintColor,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'left center',
      maskPosition: 'left center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} className={`absolute inset-0 pointer-events-none ${fade} ${green ? 'opacity-100' : 'opacity-0'}`} data-id="element-1178" data-mp="3cea623rq62wb" />
    </span>;
}